<template>
  <div
    v-if="visibleAttachments.length"
    class="ai-chat-attachments"
    :class="[
      `ai-chat-attachments--${display}`,
      { 'ai-chat-attachments--scrollable': isComposerStrip }
    ]"
  >
    <div
      ref="viewportRef"
      class="ai-chat-attachments__viewport"
      @scroll="updateScrollbar"
    >
      <div
        ref="contentRef"
        class="ai-chat-attachments__content"
      >
        <div
          v-for="attachment in visibleAttachments"
          :key="attachment.key"
          class="ai-chat-attachments__item"
          :class="{ 'ai-chat-attachments__item--draft': attachment.file }"
        >
          <AiChatAttachmentImage
            v-if="display === 'images'"
            :attachment="attachment.metadata"
            :file="attachment.file"
          />
          <FileItem
            v-else
            :file="attachment.metadata"
            :removeButton="Boolean(attachment.file && removable && !disabled)"
            :preventShow="Boolean(attachment.file)"
            :projectScoped="false"
            @remove="attachment.file && emit('remove', attachment.file)"
          />
          <button
            v-if="display === 'images' && attachment.file && removable"
            type="button"
            class="common-gray-interactive ai-chat-attachments__remove"
            :disabled="disabled"
            :aria-label="`Remove ${attachment.metadata.name}`"
            @click="emit('remove', attachment.file)"
          >
            <icon
              :data="imageRemoveIcon"
              :fill="false"
              original
              width="var(--icon-16)"
              height="var(--icon-16)"
            />
          </button>
        </div>
      </div>
    </div>
    <input
      v-if="isComposerStrip"
      type="range"
      class="ai-chat-attachments__scrollbar"
      :class="{ 'ai-chat-attachments__scrollbar--inactive': scrollMaximum === 0 }"
      :disabled="scrollMaximum === 0"
      :aria-hidden="scrollMaximum === 0"
      aria-label="Scroll attached images"
      min="0"
      :max="scrollMaximum"
      :value="scrollPosition"
      :style="{ '--attachment-scroll-thumb': `${thumbWidth}px` }"
      @input="onScrollbarInput"
    />
  </div>
</template>

<script setup lang="ts">
/** Renders removable local drafts and persisted AI attachments through the existing private file endpoints. */
import { computed, onBeforeUnmount, onMounted, onUpdated, ref } from "vue"
import { useStore } from "vuex"

import AiChatAttachmentImage from "@/components/AiSetup/AiChatAttachmentImage.vue"
import FileItem from "@/components/File/FileItem.vue"

import { isImage } from "@/helpers/isImg"
import { AiMessageAttachment, FileMetaData } from "@/models"
import { Getters } from "@/store"

const props = withDefaults(
  defineProps<{
    files?: File[]
    attachments?: AiMessageAttachment[]
    display: "images" | "documents"
    disabled?: boolean
    /** Local files in a pending sent message are previews, not editable composer selections. */
    removable?: boolean
  }>(),
  { files: () => [], attachments: () => [], disabled: false, removable: true }
)
const emit = defineEmits<{ (event: "remove", file: File): void }>()
const store = useStore()
const imageRemoveIcon = computed(() =>
  store.getters[Getters.THEME_IS_DARK]
    ? require("@icon/close3-dark.svg")
    : require("@icon/close3.svg")
)
const fileKeys = new WeakMap<File, string>()
let nextFileKey = 0

const visibleAttachments = computed(() => {
  const drafts = props.files.map(file => {
    if (!fileKeys.has(file)) fileKeys.set(file, `draft-${++nextFileKey}`)
    return {
      key: fileKeys.get(file)!,
      file,
      metadata: FileMetaData.create({
        ...new FileMetaData(),
        id: fileKeys.get(file)!,
        name: file.name,
        size: file.size,
        custom: true
      })
    }
  })
  const saved = props.attachments.map(attachment => ({
    key: attachment.id,
    file: undefined as File | undefined,
    metadata: FileMetaData.create({ ...new FileMetaData(), ...attachment })
  }))
  return [...drafts, ...saved].filter(
    attachment =>
      Boolean(isImage(attachment.metadata.name) || attachment.file?.type.startsWith("image/")) ===
      (props.display === "images")
  )
})

const isComposerStrip = computed(() => props.display === "images" && props.removable)
const viewportRef = ref<HTMLElement | null>(null)
const contentRef = ref<HTMLElement | null>(null)
const scrollMaximum = ref(0)
const scrollPosition = ref(0)
const thumbWidth = ref(121)
let resizeObserver: ResizeObserver | undefined
let observedViewport: HTMLElement | null = null
let observedContent: HTMLElement | null = null

/** Mirrors native horizontal scrolling in a persistent control even with macOS overlay scrollbars. */
function updateScrollbar() {
  const viewport = viewportRef.value
  if (!isComposerStrip.value || !viewport) return
  scrollMaximum.value = Math.max(0, viewport.scrollWidth - viewport.clientWidth)
  scrollPosition.value = viewport.scrollLeft
  thumbWidth.value = Math.min(121, viewport.clientWidth)
}

function onScrollbarInput(event: Event) {
  viewportRef.value?.scrollTo({ left: Number((event.target as HTMLInputElement).value) })
}

/** Observes viewport and content dimensions instead of watching attachment arrays. */
function observeStrip() {
  if (observedViewport !== viewportRef.value || observedContent !== contentRef.value) {
    resizeObserver?.disconnect()
    observedViewport = viewportRef.value
    observedContent = contentRef.value
    if (isComposerStrip.value) {
      resizeObserver ||= new ResizeObserver(updateScrollbar)
      if (observedViewport) resizeObserver.observe(observedViewport)
      if (observedContent) resizeObserver.observe(observedContent)
    }
  }
  updateScrollbar()
}

onMounted(observeStrip)
onUpdated(observeStrip)
onBeforeUnmount(() => resizeObserver?.disconnect())
</script>
